import React, { createContext, useContext, useState } from 'react';

// #region JSDoc Types

/**
 * @typedef {object} CurrentPageContextValue
 * @property {string} pageName - The current page name (e.g., "Checkout", "Landing Page")
 * @property {(name: string) => void} setPageName - Updates the current page name
 */

// #endregion

/**
 * Context for sharing the current page name across the app.
 *
 * - PageTracker writes to this when a page renders
 * - Header (or other components) read from it reactively
 *
 * This ensures Header shows the correct page name even during redirects,
 * because the context only updates when PageTracker actually mounts.
 *
 * @type {React.Context<CurrentPageContextValue>}
 */
const CurrentPageContext = createContext(
  /** @type {CurrentPageContextValue} */ ({
    pageName: '',
    setPageName: () => {},
  }),
);

/**
 * Provider that manages the current page name state.
 * Wrap this around your router so all routes and layout components can access it.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 *
 * @example
 * <CurrentPageProvider>
 *   <AppRouter />
 * </CurrentPageProvider>
 */
export function CurrentPageProvider({ children }) {
  const [pageName, setPageName] = useState('');

  return (
    <CurrentPageContext.Provider value={{ pageName, setPageName }}>
      {children}
    </CurrentPageContext.Provider>
  );
}

export function useCurrentPage() {
  const context = useContext(CurrentPageContext);
  if (context === undefined) {
    throw new Error('useCurrentPage must be used within a CurrentPageProvider');
  }
  return context;
}
